import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment/moment';
import 'moment-timezone';
import translate from '../../../../commonComponents/translate';
import BonusListItemDataProp, {
  BonusListItemBlock,
  BonusListItemStatus,
  RewardTypes,
} from '../../propTypes/bonusListItemDataProp';
import CheckIcon from '../../../../commonComponents/icons/CheckIcon';
import CrossIcon from '../../../../commonComponents/icons/CrossIcon';
import { formatNumbersWithZero } from '../../../../services/utilityFunctions';

function BonusListDataItemBlock(props) {
  const { t, blockType, items, onClickSeeAll } = props;

  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const renderTitle = item => {
    let content;
    switch (blockType) {
      case BonusListItemBlock.STARTING_BONUS:
        content = t(`report.pureliink.subBlock.title.${item.type}`);
        break;
      case BonusListItemBlock.DESTINATION:
        content = `${moment(item.startDate)
          .tz('Europe/Luxembourg')
          .format('D MMM')} - ${moment(item.endDate)
          .tz('Europe/Luxembourg')
          .format('D MMM YYYY')}`;
        break;
      default:
        return null;
    }

    return <span className="item-title">{content}</span>;
  };

  const renderRemainingDays = item => {
    if (!item?.remainingDays) return null;
    return (
      <span className="remaining-days">
        {`${item.remainingDays} ${t(
          'report.pureliink.subBlock.remainingDays',
        )}`}
      </span>
    );
  };

  const renderItemReward = (item, rewardType) => {
    let content;
    switch (rewardType) {
      case RewardTypes.YOUR_VA:
        content = item.VARewards.map(reward => (
          <div className="reward-block">
            <span className="reward-text">{`${reward.minimumVA} ${t(
              'report.pureliink.subBlock.VA',
            )} - ${reward.coinReward} ${t(
              'report.pureliink.subBlock.coins',
            )}`}</span>
          </div>
        ));
        break;
      case RewardTypes.RECRUITMENT:
        content = (
          <div className="reward-block">
            <span className="reward-text">{`${
              item.recruitmentRewards.coinsPerRecruitment
            } ${t('report.pureliink.subBlock.coinsPerRecruitment')}`}</span>
          </div>
        );
        break;
      case RewardTypes.COMMISSIONS:
        content = item.commissionRewards.map(commissionReward => (
          <div className="reward-block">
            <span className="reward-text">{`${formatNumbersWithZero(
              commissionReward.minimumCommission,
              true,
              false,
            )} - ${formatNumbersWithZero(
              commissionReward.commissionReward,
              true,
              false,
            )}${commissionReward?.currencyInfo?.symbol || '€'}`}</span>
          </div>
        ));
        break;
      case RewardTypes.GROUP_VA:
        content = item.commissionRewards.map(commissionReward => {
          if (!commissionReward.voucherUrl) return null;
          return (
            <img
              className="voucher-image"
              src={commissionReward.voucherUrl}
              alt=""
            />
          );
        });
        break;
      default:
        return null;
    }

    return (
      <>
        <span className={`item-reward ${rewardType}`}>
          {t('report.pureliink.subBlock.reward')}:
        </span>
        <div className="d-flex mx-2 reward-block-list">{content}</div>
      </>
    );
  };

  const renderStatusIcon = status => {
    let content;

    switch (status) {
      case BonusListItemStatus.COMPLETED:
        content = <CheckIcon color="#3FBC5F" />;
        break;
      case BonusListItemStatus.INCOMPLETE:
        content = <CrossIcon color="#E15151" />;
        break;
      case BonusListItemStatus.UNSTARTED:
      default:
        content = null;
        break;
    }

    return (
      <div className={`completedStageCheck ${status || ''}`}>{content}</div>
    );
  };

  const renderItemStatus = (item, rewardType) => (
    <>
      <div className="d-flex">
        <span className="item-status-text">
          {t(`report.pureliink.subBlock.status.${rewardType}.title`)}
        </span>
      </div>
      <div>
        <p>
          {t(
            `report.pureliink.subBlock.status.${item.type}.${rewardType}.text`,
          )}
        </p>
      </div>
      <div className="d-flex">{renderItemReward(item, rewardType)}</div>
    </>
  );

  const renderProgressBar = (item, rewardType) => {
    switch (rewardType) {
      case RewardTypes.YOUR_VA: {
        const { VARewards: stages, currentVA } = item;
        // Determine the maximum VA which is the minimumVA of the last stage
        const maxVA = stages[stages.length - 1].minimumVA;

        // Calculate the width percentage for each stage
        const stageWidths = stages.map((stage, index) => {
          if (index === 0) {
            return (stage.minimumVA / maxVA) * 100;
          }
          const previousVA = stages[index - 1].minimumVA;
          return ((stage.minimumVA - previousVA) / maxVA) * 100;
        });

        return (
          <div className="d-flex">
            <div className="progress-bar-container">
              {stages.map((stage, index) => {
                // Determine the portion of the current stage that is filled
                const previousVA = index > 0 ? stages[index - 1].minimumVA : 0;
                const stageProgress =
                  Math.min(
                    Math.max(currentVA - previousVA, 0) /
                      (stage.minimumVA - previousVA),
                    1,
                  ) * 100;

                return (
                  <div
                    className={`stage alternate-color-${index % 2}`}
                    style={{
                      width: `${stageWidths[index]}%`,
                      background: `linear-gradient(to right, ${
                        index % 2 === 0 ? '#72DA44' : '#5ABF2E'
                      } ${stageProgress}%, ${
                        index % 2 === 0 ? '#EDEDED' : '#DDDCDC'
                      } ${stageProgress}%)`,
                    }}
                  >
                    <span className="stage-text">{stage.minimumVA}</span>
                  </div>
                );
              })}
            </div>
            <span className="progress-now">{`${t('now')}: ${currentVA}${t(
              'report.pureliink.subBlock.VA',
            )}`}</span>
          </div>
        );
      }
      case RewardTypes.RECRUITMENT: {
        const { recruitmentRewards } = item;
        const { maximumRecruitments, totalRecruitment } = recruitmentRewards;

        // Calculate the width percentage for each stage
        const stageWidth = 100 / maximumRecruitments;

        return (
          <div className="progress-bar-container">
            {Array.from({ length: maximumRecruitments }).map((_, index) => {
              const stageProgress =
                Math.min(Math.max(totalRecruitment - index, 0), 1) * 100;

              return (
                <div
                  className={`stage alternate-color-${index % 2}`}
                  style={{
                    width: `${stageWidth}%`,
                    background: `linear-gradient(to right, ${
                      index % 2 === 0 ? '#72DA44' : '#5ABF2E'
                    } ${stageProgress}%, ${
                      index % 2 === 0 ? '#EDEDED' : '#DDDCDC'
                    } ${stageProgress}%)`,
                  }}
                >
                  <span style={{ position: 'relative', zIndex: 1 }}>
                    {index + 1}
                  </span>
                </div>
              );
            })}
          </div>
        );
      }
      case RewardTypes.COMMISSIONS: {
        const { commissionRewards: stages, currentCommission } = item;
        let stageWidths = [];
        if (stages.length > 0) {
          const maxCommission = stages[stages.length - 1].minimumCommission;

          // Calculate the width percentage for each stage
          stageWidths = stages.map((stage, index) => {
            if (index === 0) {
              return (stage.minimumCommission / maxCommission) * 100;
            }
            const previousCommission = stages[index - 1].minimumCommission;
            return (
              ((stage.minimumCommission - previousCommission) / maxCommission) *
              100
            );
          });
        }

        return (
          <div className="d-flex">
            <div className="progress-bar-container">
              {stages.map((stage, index) => {
                // Determine the portion of the current stage that is filled
                const previousCommission =
                  index > 0 ? stages[index - 1].minimumCommission : 0;
                const stageProgress =
                  Math.min(
                    Math.max(currentCommission - previousCommission, 0) /
                      (stage.minimumCommission - previousCommission),
                    1,
                  ) * 100;

                return (
                  <div
                    className={`stage alternate-color-${index % 2}`}
                    style={{
                      width: `${stageWidths[index]}%`,
                      background: `linear-gradient(to right, ${
                        index % 2 === 0 ? '#72DA44' : '#5ABF2E'
                      } ${stageProgress}%, ${
                        index % 2 === 0 ? '#EDEDED' : '#DDDCDC'
                      } ${stageProgress}%)`,
                    }}
                  >
                    <span className="stage-text">
                      {formatNumbersWithZero(
                        stage.minimumCommission,
                        true,
                        false,
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            <span className="progress-now">{`${t(
              'now',
            )}: ${formatNumbersWithZero(
              currentCommission,
              true,
              false,
            )}`}</span>
          </div>
        );
      }
      case RewardTypes.GROUP_VA: {
        const { commissionRewards: stages, groupVA } = item;
        let maxVA;
        let stageWidths = [];
        if (stages.length > 0) {
          // Determine the maximum VA which is the minimumVA of the last stage
          maxVA = stages[stages.length - 1].minimumGroupVA;

          // Calculate the width percentage for each stage
          stageWidths = stages.map((stage, index) => {
            if (index === 0) {
              return (stage.minimumGroupVA / maxVA) * 100;
            }
            const previousVA = stages[index - 1].minimumGroupVA;
            return ((stage.minimumGroupVA - previousVA) / maxVA) * 100;
          });
        }

        const filteredStages = stages.filter(
          (stage, index, self) =>
            index ===
            self.findIndex(XD => XD.minimumGroupVA === stage.minimumGroupVA),
        );

        return (
          <div className="d-flex">
            <div className="progress-bar-container">
              {filteredStages.map((stage, index) => {
                // Determine the portion of the current stage that is filled
                const previousVA =
                  index > 0 ? stages[index - 1].minimumGroupVA : 0;
                const stageProgress =
                  Math.min(
                    Math.max(groupVA - previousVA, 0) /
                      (stage.minimumGroupVA - previousVA),
                    1,
                  ) * 100;

                return (
                  <div
                    className={`stage alternate-color-${index % 2}`}
                    style={{
                      width: `${stageWidths[index]}%`,
                      background: `linear-gradient(to right, ${
                        index % 2 === 0 ? '#72DA44' : '#5ABF2E'
                      } ${stageProgress}%, ${
                        index % 2 === 0 ? '#EDEDED' : '#DDDCDC'
                      } ${stageProgress}%)`,
                    }}
                  >
                    <span className="stage-text">
                      {formatNumbersWithZero(
                        stage.minimumGroupVA,
                        false,
                        false,
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            <span className="progress-now">{`${t(
              'now',
            )}: ${formatNumbersWithZero(groupVA, false, false)}`}</span>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const renderYourVA = item => {
    if (blockType !== BonusListItemBlock.STARTING_BONUS) return null;

    return (
      <div className="d-flex">
        <div className="col-1 px-0">{renderStatusIcon(item.VAStatus)}</div>
        <div className="col-5 pl-0">
          {renderItemStatus(item, RewardTypes.YOUR_VA)}
        </div>
        <div className="col-6 my-auto">
          {renderProgressBar(item, RewardTypes.YOUR_VA)}
        </div>
      </div>
    );
  };

  const renderRecruitment = item => {
    if (blockType !== BonusListItemBlock.STARTING_BONUS) return null;

    return (
      <div className="d-flex">
        <div className="col-1 px-0">
          {renderStatusIcon(item.recruitmentStatus)}
        </div>
        <div className="col-5 pl-0">
          {renderItemStatus(item, RewardTypes.RECRUITMENT)}
        </div>
        <div className="col-6 my-auto">
          {renderProgressBar(item, RewardTypes.RECRUITMENT)}
        </div>
      </div>
    );
  };

  const renderCommissions = item => {
    if (blockType !== BonusListItemBlock.JACKPOT) return null;

    return (
      <div className="d-flex">
        <div className="col-1 px-0">{renderStatusIcon(item.status)}</div>
        <div className="col-5 pl-0">
          {renderItemStatus(item, RewardTypes.COMMISSIONS)}
        </div>
        <div className="col-6 my-auto">
          {renderProgressBar(item, RewardTypes.COMMISSIONS)}
        </div>
      </div>
    );
  };

  const renderGroupVA = item => {
    if (blockType !== BonusListItemBlock.DESTINATION) return null;

    return (
      <div className="d-flex">
        <div className="col-1 px-0">{renderStatusIcon(item.status)}</div>
        <div className="col-5 pl-0">
          {renderItemStatus(item, RewardTypes.GROUP_VA)}
        </div>
        <div className="col-6 my-auto">
          {renderProgressBar(item, RewardTypes.GROUP_VA)}
        </div>
      </div>
    );
  };

  return (
    <div className="bonus-list-data-item-card pb-2 mb-3">
      <div className="d-flex px-2 bonus-list-data-item-card-title">
        <button
          className="title-button bonus-list-button"
          type="button"
          onClick={toggleCollapse}
        >
          {isCollapsed ? (
            <i className="fa fa-solid fa-chevron-up" />
          ) : (
            <i className="fa fa-solid fa-chevron-down" />
          )}
        </button>
        <span className="title-text">
          {t(`report.pureliink.block.title.${blockType}`)}
        </span>
      </div>
      <div
        className={cx('bonus-list-data-item-card-body m-3', {
          collapsed: isCollapsed,
        })}
      >
        {items.map(item => (
          <div className="card">
            <div className="d-flex justify-content-between px-3 sub-title-block">
              <div className="sub-title-text">
                {renderTitle(item)}
                {renderRemainingDays(item)}
              </div>
              <div className="d-flex">
                <button
                  className="see-all-button bonus-list-button"
                  type="button"
                  onClick={() => onClickSeeAll(item)}
                >
                  {t('report.pureliink.block.my_affiliates')}
                  <i className="ml-2 fa fa-solid fa-chevron-right" />
                </button>
              </div>
            </div>
            <div className="sub-card-body">
              {renderYourVA(item)}
              {renderRecruitment(item)}
              {renderCommissions(item)}
              {renderGroupVA(item)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default translate(BonusListDataItemBlock);

BonusListDataItemBlock.propTypes = {
  t: PropTypes.func.isRequired,
  blockType: PropTypes.oneOf([
    BonusListItemBlock.STARTING_BONUS,
    BonusListItemBlock.JACKPOT,
    BonusListItemBlock.DESTINATION,
  ]).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape(BonusListItemDataProp.propType))
    .isRequired,
  onClickSeeAll: PropTypes.func.isRequired,
};
